/**
 * Site sohbetleri (v0.1.248; Sercan, 29 Oct 2026: "Sitede biri yazdığında görmek isterim"): GET /site-chats every time
 * the screen opens, the newest chats first; "Daha eski sohbetler" appends the page before the oldest one. Read only.
 */

import type { SiteChatList, SiteChatThread, SiteFormRequestView } from '@twin/contracts';
import { useSyncExternalStore } from 'react';
import type { TwinApi } from '../api/client.js';
import { useStore } from './context.js';

export interface SiteChatsState {
  chats: SiteChatThread[] | null;
  /** v0.1.256: the newest site form requests (from the first page). */
  forms: SiteFormRequestView[];
  more: boolean;
  error: string | null;
  loading: boolean;
}

export class SiteChatsStore {
  private state: SiteChatsState = { chats: null, forms: [], more: false, error: null, loading: false };
  private inFlight: Promise<void> | null = null;
  private readonly listeners = new Set<() => void>();

  constructor(private readonly api: TwinApi) {}

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly getState = (): SiteChatsState => this.state;

  private set(patch: Partial<SiteChatsState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }

  private run(read: () => Promise<SiteChatList>, append: boolean): Promise<void> {
    if (this.inFlight !== null) return this.inFlight;
    this.set({ loading: true });
    const run = (async () => {
      try {
        const page = await read();
        const known = new Set((append ? (this.state.chats ?? []) : []).map((chat) => chat.chatId));
        const chats = append
          ? [...(this.state.chats ?? []), ...page.chats.filter((chat) => !known.has(chat.chatId))]
          : page.chats;
        this.set({
          chats,
          forms: append ? this.state.forms : page.forms,
          more: page.more,
          error: null,
          loading: false,
        });
      } catch (error) {
        this.set({ error: error instanceof Error ? error.message : String(error), loading: false });
      }
    })();
    this.inFlight = run;
    void run.finally(() => {
      if (this.inFlight === run) this.inFlight = null;
    });
    return run;
  }

  /** The newest page again; the screen calls it on mount and on Yenile. */
  reload(): Promise<void> {
    return this.run(() => this.api.getSiteChats(), false);
  }

  /** The page before the oldest chat shown. */
  older(): Promise<void> {
    const oldest = this.state.chats?.at(-1);
    if (oldest === undefined) return Promise.resolve();
    return this.run(() => this.api.getSiteChats(oldest.startedAt), true);
  }
}

export function useSiteChats(): { store: SiteChatsStore; state: SiteChatsState } {
  const store = useStore().siteChats;
  const state = useSyncExternalStore(store.subscribe, store.getState, store.getState);
  return { store, state };
}
